import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-camera',
  templateUrl: './camera.component.html',
  styleUrls: ['./camera.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CameraComponent implements OnDestroy {
  visible: boolean;
  imageUrl: string;

  @Input('videoVisible') set videoVisible(visible: boolean) {
    this.visible = visible;
    if (this.visible && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.getDeviceAndStream();
    }
  }

  @Input() public width: number;
  @Input() public height: number;

  @Output()
  closeCamera = new EventEmitter<any>();

  @Output() cancel = new EventEmitter<boolean>();

  @ViewChild('video', { static: true })
  public video: ElementRef;

  @ViewChild('canvas', { static: true })
  public canvas: ElementRef;
  public facingMode: 'user' | 'environment' = 'environment';

  constructor(private sanitizer: DomSanitizer) {
    this.imageUrl = null;
    this.width = 640;
    this.height = 480;
  }

  ngOnDestroy(): void {
    this.stopVideo();
  }

  switchCamera() {
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    this.stopVideo();
    this.getDeviceAndStream();
  }

  async onCapture() {
    this.canvas.nativeElement.getContext('2d').drawImage(this.video.nativeElement, 0, 0, this.width, this.height);

    const blob = await this.canvasToBlob(this.canvas.nativeElement, 'image/jpeg', 0.95);
    this.imageUrl = URL.createObjectURL(blob);
    this.stopVideo();
    this.closeCamera.emit(blob);
  }

  readFile(e: Event) {
    const file: File = (e.target as HTMLInputElement).files[0];

    if (!file) {
      return;
    }

    this.closeCamera.emit(file);
    this.stopVideo();

    // remove video to display background image
    this.video.nativeElement.srcObject = null;

    this.imageUrl = URL.createObjectURL(file);
  }

  private stopVideo(): void {
    if (this.video.nativeElement.srcObject) {
      this.video.nativeElement.srcObject.getTracks().forEach((track) => track.stop());
    }
  }

  private canvasToBlob(canvas: any, contentType: string, quality?: number): Promise<Blob | File> {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, contentType, quality);
    });
  }

  private async getDeviceAndStream(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((device) => device.kind === 'videoinput');

    if (videoDevices.length === 0) {
      return;
    }

    const constraints = {
      video: {
        facingMode: this.facingMode,
      },
      audio: false,
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.video.nativeElement.srcObject = stream;
    this.video.nativeElement.play();
  }
}
